import { useCallback, useEffect, useState } from 'react'
import {
  type ApiDocumentRequest,
  type ApiDocumentRequestStatus,
  type ApiMe,
  fetchDocumentRequests,
} from '@/fetcher'
import {
  DOCUMENT_REQUEST_STATUS_LABELS,
  formatDateTime,
  formatRub,
  formatUsdt,
} from '@/helpers/format'
import { useNav } from '@/nav'
import { TelegramMainButton } from './TelegramMainButton'

const PAGE_SIZE = 20

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Все статусы' },
  { value: 'created', label: 'Создано' },
  { value: 'client_paid', label: 'Оплачено' },
  { value: 'bank_received', label: 'Зачислено' },
  { value: 'cancelled', label: 'Отменено' },
]

type RequestsListPageProps = {
  me: ApiMe
}

export const RequestsListPage = ({ me }: RequestsListPageProps) => {
  const { navigate, getViewState, setViewState } = useNav()
  const saved = getViewState('requests-list') ?? {}
  const [status, setStatus] = useState<ApiDocumentRequestStatus | ''>(
    (saved.status as ApiDocumentRequestStatus | '') ?? '',
  )
  const [teamId, setTeamId] = useState((saved.teamId as string) ?? '')
  const [searchInput, setSearchInput] = useState(
    (saved.searchInput as string) ?? '',
  )
  const [searchQuery, setSearchQuery] = useState(
    (saved.searchQuery as string) ?? '',
  )
  const [requests, setRequests] = useState<ApiDocumentRequest[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const trimmed = searchInput.trim()
    if (trimmed.length > 0 && trimmed.length < 2) return
    const timer = setTimeout(() => setSearchQuery(trimmed), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setViewState('requests-list', { status, teamId, searchInput, searchQuery })
  }, [status, teamId, searchInput, searchQuery, setViewState])

  const loadFirst = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)
    setOffset(0)
    try {
      const { requests: rows, hasMore: more } = await fetchDocumentRequests({
        status: status || undefined,
        teamId: teamId || undefined,
        search: searchQuery.length >= 2 ? searchQuery : undefined,
        limit: PAGE_SIZE,
        offset: 0,
      })
      setRequests(rows)
      setHasMore(more)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Не удалось загрузить заявки',
      )
      setRequests([])
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }, [status, teamId, searchQuery])

  useEffect(() => {
    void loadFirst()
  }, [loadFirst])

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    try {
      const nextOffset = offset + PAGE_SIZE
      const { requests: rows, hasMore: more } = await fetchDocumentRequests({
        status: status || undefined,
        teamId: teamId || undefined,
        search: searchQuery.length >= 2 ? searchQuery : undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
      })
      setRequests((prev) => {
        const seen = new Set(prev.map((r) => r.id))
        const merged = [...prev]
        for (const row of rows) {
          if (!seen.has(row.id)) {
            merged.push(row)
            seen.add(row.id)
          }
        }
        return merged
      })
      setOffset(nextOffset)
      setHasMore(more)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Не удалось загрузить заявки',
      )
    } finally {
      setIsLoadingMore(false)
    }
  }, [hasMore, isLoadingMore, offset, status, teamId, searchQuery])

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.currentTarget.value as ApiDocumentRequestStatus | '')
  }

  const handleTeamChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTeamId(event.currentTarget.value)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.currentTarget.value)
  }

  const isEmpty = !isLoading && requests.length === 0

  return (
    <>
      <TelegramMainButton
        isVisible
        isLoading={false}
        text="Создать заявку"
        onClick={() => navigate('docs')}
      />
      <div className="py-8 mx-auto max-w-2xl px-4 pb-40">
        <div className="mb-4 space-y-2">
          <input
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Поиск по ФИО, инвойсу или договору"
            className="w-full rounded-[22px] bg-[var(--app-input)] px-4 py-3 text-sm leading-6 text-[var(--app-text)] outline-none transition focus:border-[var(--app-accent)] focus:ring-4 focus:ring-[var(--app-accent-soft)]"
          />

          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <select
                className="w-full appearance-none rounded-[22px] bg-[var(--app-input)] px-4 py-3 pr-10 text-sm leading-6 text-[var(--app-text)] outline-none transition focus:border-[var(--app-accent)] focus:ring-4 focus:ring-[var(--app-accent-soft)]"
                value={status}
                onChange={handleStatusChange}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--app-muted)]">
                ↓
              </span>
            </div>

            <div className="relative">
              <select
                className="w-full appearance-none rounded-[22px] bg-[var(--app-input)] px-4 py-3 pr-10 text-sm leading-6 text-[var(--app-text)] outline-none transition focus:border-[var(--app-accent)] focus:ring-4 focus:ring-[var(--app-accent-soft)]"
                value={teamId}
                onChange={handleTeamChange}
              >
                <option value="">Все команды</option>
                {me.teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--app-muted)]">
                ↓
              </span>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
            {errorMessage}
          </div>
        )}

        {isEmpty && (
          <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
            Заявки не найдены
          </div>
        )}

        <div className="space-y-2">
          {requests.map((request) => (
            <button
              key={request.id}
              type="button"
              onClick={() =>
                navigate('request-info', { params: { requestId: request.id } })
              }
              className="w-full rounded-[18px] border border-transparent bg-[var(--app-input)] px-4 py-3 text-left transition hover:opacity-90"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-semibold text-[var(--app-text)]">
                    {request.data.fullNameRu}
                  </p>
                  <p className="text-xs text-[var(--app-muted)]">
                    {request.contractNo} · {request.invoiceNo}
                  </p>
                  <p className="text-xs text-[var(--app-muted)]">
                    {formatDateTime(request.createdAt)} · {request.teamName}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1 self-stretch">
                  <p className="text-sm font-semibold text-[var(--app-text)]">
                    {formatRub(request.amountRub)} ₽
                  </p>
                  <p className="text-xs text-[var(--app-muted)]">
                    {formatUsdt(request.clientPaidUsdtAmount)} ₮
                  </p>
                  <p className="mt-auto text-xs font-medium text-[var(--app-accent)]">
                    {DOCUMENT_REQUEST_STATUS_LABELS[request.status]}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {hasMore && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={isLoadingMore}
            className="mt-3 w-full rounded-[18px] bg-[var(--app-input)] px-4 py-3 text-sm font-medium text-[var(--app-text)] transition hover:opacity-80 disabled:opacity-50"
          >
            {isLoadingMore ? 'Загрузка...' : 'Загрузить ещё'}
          </button>
        )}
      </div>
    </>
  )
}
